import React, { useEffect } from 'react';
import Layout from '../components/Layout';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Search } from 'lucide-react';
import { Input } from "../components/ui/input";

const FAQFR = () => {
  useEffect(() => {
    document.title = 'Internjin | FAQ';
  }, []);

  const faqs = [
    {
      category: "Général",
      items: [
        { q: "Internjin est-elle une agence de recrutement ?", a: "Non. Internjin est une plateforme de matching dédiée exclusivement aux stages, avec une composante d'accompagnement pédagogique. L'objectif est la formation et l'échange interculturel, pas le placement permanent." },
        { q: "Depuis quand Internjin existe-t-elle ?", a: "Internjin est une startup en phase de lancement, fondée par deux étudiants français sur la base de leurs propres expériences de stage au Japon. Sushi Tech Tokyo est le lieu où les premiers partenaires japonais sont recherchés." },
        { q: "Êtes-vous soutenus par des institutions françaises ?", a: "Internjin travaille en lien avec des établissements d'enseignement supérieur français et recherche activement des partenariats institutionnels. Le modèle est entièrement aligné sur les exigences légales françaises en matière de stages." }
      ]
    },
    {
      category: "Étudiants",
      items: [
        { q: "Quel est le niveau des étudiants que vous proposez ?", a: "Les étudiants proviennent d'écoles de commerce, d'écoles d'ingénieurs, d'universités et d'institutions spécialisées françaises — du niveau Bachelor (Bac+3) au niveau Master (Bac+5), dans la tech, le design, le marketing, la finance, les RH, et plus encore." },
        { q: "Parlent-ils japonais ?", a: "Certains oui, mais ce n'est pas obligatoire. Les étudiants capables de travailler en anglais sont également sélectionnés. Les candidatures peuvent être filtrées par critères linguistiques, et tous les étudiants reçoivent une préparation culturelle sur le Japon avant leur départ." },
        { q: "L'étudiant viendra-t-il seul au Japon ?", a: "Oui. L'étudiant gère son propre voyage et son logement, avec l'aide d'Internjin en amont. Internjin peut également mettre en relation les entreprises avec des ressources locales pour faciliter l'accueil et l'intégration de l'étudiant." },
        { q: "Que se passe-t-il si le stage ne se passe pas bien ?", a: "Internjin agit en tant que médiateur et intervient rapidement pour trouver une solution satisfaisante pour les deux parties. Dans les cas extrêmes, une fin anticipée du stage peut être organisée selon les termes de la convention." },
        { q: "Comment les étudiants français gèrent-ils la culture de travail japonaise ?", a: "Les étudiants français ont tendance à être directs et proactifs, ce qui peut différer des normes japonaises. Tous les étudiants reçoivent une préparation interculturelle couvrant les styles de communication, la hiérarchie, la ponctualité et l'étiquette professionnelle. Internjin reste disponible tout au long du stage pour soutenir les deux parties." },
        { q: "Et si l'étudiant a un accident ou tombe malade au Japon ?", a: "L'étudiant doit avoir une assurance santé et voyage personnelle organisée avant le départ. L'entreprise n'est pas responsable de la couverture santé personnelle. Les accidents du travail suivent les responsabilités décrites dans la convention de stage, et Internjin peut conseiller les deux parties." },
        { q: "Peut-on recruter l'étudiant définitivement après le stage ?", a: "Absolument — et cela arrive. Si les deux parties sont intéressées, l'entreprise peut offrir un poste à temps plein à la fin du stage. Internjin soutient pleinement ce résultat." }
      ]
    },
    {
      category: "Modèle Économique",
      items: [
        { q: "Combien cela coûte-t-il pour l'entreprise ?", a: "Il n'y a aucune obligation légale de payer l'étudiant en vertu de la loi japonaise, mais offrir une gratification est fortement recommandé pour attirer les meilleurs candidats. Des frais de service sont également payés à Internjin pour le matching et le suivi ; une grille tarifaire détaillée est fournie lors de la réunion d'introduction." },
        { q: "L'étudiant est-il un employé de l'entreprise ?", a: "Non. L'étudiant est régi par une convention de stage — aucun contrat de travail n'est requis. La convention de stage est le seul document juridique nécessaire." },
        { q: "Quelle est la durée minimale et maximale d'un stage ?", a: "Les stages varient de 2 mois à 1 an, selon la disponibilité de l'étudiant, les besoins de l'entreprise et la portée du projet." }
      ]
    },
    {
      category: "Étapes Pratiques",
      items: [
        { q: "Quelles démarches l'entreprise doit-elle entreprendre ?", a: "Définissez vos besoins et les profils souhaités, signez la convention de stage, désignez un mentor interne et accueillez l'étudiant. Internjin gère la sélection des candidats, la gestion documentaire et le soutien continu." },
        { q: "Combien de temps prend le processus de matching ?", a: "La sélection et le matching prennent généralement 2 à 4 semaines à partir du moment où l'offre est publiée. Internjin recommande de planifier au moins 6 à 8 semaines avant la date de début souhaitée pour tenir compte des délais de visa et des calendriers académiques." },
        { q: "Peut-on prolonger ou renouveler le stage si cela se passe bien ?", a: "Oui, tant que cela reste dans le cadre académique de l'étudiant et que les deux parties sont d'accord. Toute prolongation doit être formalisée par un avenant à la convention de stage originale, qu'Internjin gérera." },
        { q: "L'entreprise doit-elle parler français ?", a: "Non. Internjin fournit une médiation bilingue, et tous les documents peuvent être fournis en anglais ou en japonais." },
        { q: "Comment démarrer un partenariat avec Internjin ?", a: "Contactez-nous simplement pour planifier un appel de découverte sans engagement. L'équipe élaborera une proposition adaptée aux besoins de l'entreprise." }
      ]
    }
  ];

  return (
    <Layout>
      <section className="bg-slate-900 text-white py-20">
        <div className="container mx-auto px-4 md:px-6 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Centre d'Aide</h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto mb-8">
            Trouvez des réponses aux questions fréquentes sur Internjin, le processus de recrutement et nos étudiants.
          </p>
          <div className="max-w-xl mx-auto relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input 
              placeholder="Rechercher des réponses..." 
              className="pl-10 h-12 bg-white/10 border-slate-700 text-white placeholder:text-slate-400 focus-visible:ring-red-500 rounded-full" 
            />
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <Tabs defaultValue="Général" className="space-y-8">
            <div className="flex justify-center overflow-x-auto pb-4">
              <TabsList className="bg-white p-1 rounded-full border border-slate-200 shadow-sm h-auto inline-flex">
                {faqs.map((cat) => (
                  <TabsTrigger 
                    key={cat.category} 
                    value={cat.category}
                    className="rounded-full px-6 py-3 data-[state=active]:bg-slate-900 data-[state=active]:text-white text-slate-600 font-medium transition-all"
                  >
                    {cat.category}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            {faqs.map((cat) => (
              <TabsContent key={cat.category} value={cat.category} className="space-y-6 animate-in fade-in-50 slide-in-from-bottom-4 duration-500">
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                  <div className="p-6 border-b border-slate-100 bg-slate-50/50">
                    <h2 className="text-2xl font-bold text-slate-900">FAQ {cat.category}</h2>
                  </div>
                  <div className="p-6">
                    <Accordion type="single" collapsible className="w-full space-y-4">
                      {cat.items.map((item, j) => (
                        <AccordionItem key={j} value={`item-${j}`} className="border rounded-lg px-4 data-[state=open]:bg-slate-50 transition-colors border-slate-200">
                          <AccordionTrigger className="text-left font-semibold text-slate-800 hover:text-red-600 hover:no-underline py-4">
                            {item.q}
                          </AccordionTrigger>
                          <AccordionContent className="text-slate-600 leading-relaxed pb-4">
                            {item.a}
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>
    </Layout>
  );
};

export default FAQFR;
